import { h } from './dom';
import { createMap, pinIcon } from './maps';

/** Numbered markers joined by a line, from given coordinates (D65): the drawing behind Today's stop
 *  map and Plan tomorrow's. One Map per element, created on the first draw. */

export interface MapPoint {
  position: google.maps.LatLngLiteral;
  label: string;
}

const boundsOf = (points: readonly MapPoint[]): google.maps.LatLngBoundsLiteral => ({
  north: Math.max(...points.map((p) => p.position.lat)),
  south: Math.min(...points.map((p) => p.position.lat)),
  east: Math.max(...points.map((p) => p.position.lng)),
  west: Math.min(...points.map((p) => p.position.lng)),
});

export const pointsMap = (
  ariaLabel: string,
): {
  el: HTMLElement;
  draw: (points: readonly MapPoint[]) => Promise<void>;
  clear: () => void;
  hasMap: () => boolean;
} => {
  const el = h('div', { class: 'stopmap', hidden: true, role: 'img', 'aria-label': ariaLabel });
  let map: google.maps.Map | null = null;
  let mapPromise: Promise<google.maps.Map> | null = null;
  let drawn: { setMap: (m: google.maps.Map | null) => void }[] = [];

  const clear = () => {
    for (const d of drawn) d.setMap(null);
    drawn = [];
  };

  const draw = async (points: readonly MapPoint[]) => {
    const first = points[0];
    if (!first) return;
    // one Map per element: a draw that overlaps the first (the boot sync re-render) waits for the
    // same creation instead of mounting a second Map on the same div
    mapPromise ??= createMap(el, first.position, 13).catch((err: unknown) => {
      mapPromise = null; // a failed load may be retried on the next draw
      throw err;
    });
    map = await mapPromise;
    const { Marker } = await google.maps.importLibrary('marker');
    const { Polyline } = await google.maps.importLibrary('maps');
    clear();
    for (const p of points)
      drawn.push(
        new Marker({
          map,
          position: p.position,
          icon: pinIcon('in_pipeline', false, p.label),
          title: p.label,
        }),
      );
    if (points.length > 1) {
      drawn.push(
        new Polyline({
          map,
          path: points.map((p) => p.position),
          strokeColor: '#12a580',
          strokeOpacity: 0.9,
          strokeWeight: 4,
          clickable: false,
        }),
      );
      map.fitBounds(boundsOf(points), 40);
    } else map.setCenter(first.position);
  };

  return { el, draw, clear, hasMap: () => map !== null };
};
